// Pure rules behind the product form: which fields show for each product
// kind, the kind/journey pairing, and the "second base product" warning.
// Kept free of React so they can be unit-tested.

export type ProductKind = 'journey' | 'base';
export type ProductBilling = 'one_time' | 'monthly';

export const DEFAULT_TRIAL_DAYS = 7;

type KindAware = { kind?: ProductKind | null };

/** The journey picker belongs to journey products; trial days to the base product. */
export function isProductFieldVisible(key: string, values: KindAware): boolean {
  const kind = values.kind ?? 'journey';
  if (key === 'journeyId') return kind === 'journey';
  if (key === 'trialDays') return kind === 'base';
  return true;
}

/** A journey product must point at a journey; the base product never does. */
export function productKindError(values: KindAware & { journeyId?: string | null }): 'journeyRequired' | null {
  const kind = values.kind ?? 'journey';
  if (kind === 'journey' && !values.journeyId) return 'journeyRequired';
  return null;
}

/**
 * Values to send for kind-dependent fields: base → no journey, keeps the
 * trial; journey → keeps the journey, no trial. Billing is whatever was
 * chosen (a journey product may also be monthly one day).
 */
export function normalizeKindFields(values: {
  kind?: ProductKind | null;
  billing?: ProductBilling | null;
  journeyId?: string | null;
  trialDays?: number | null;
}): { kind: ProductKind; billing: ProductBilling; journeyId: string | null; trialDays: number | null } {
  const kind = values.kind ?? 'journey';
  const billing = values.billing ?? (kind === 'base' ? 'monthly' : 'one_time');
  if (kind === 'base') {
    return { kind, billing, journeyId: null, trialDays: values.trialDays ?? null };
  }
  return { kind, billing, journeyId: values.journeyId ?? null, trialDays: null };
}

/**
 * Another ACTIVE base product (not the one being edited) already exists —
 * the store only shows one, so the admin should know.
 */
export function findOtherActiveBase<T extends { productId: string; kind?: ProductKind | null; isActive: boolean }>(
  products: T[],
  currentProductId: string | undefined,
): T | null {
  return (
    products.find(
      (product) => product.kind === 'base' && product.isActive && product.productId !== currentProductId,
    ) ?? null
  );
}

/** Store order for the admin list: the base product first, then by name. */
export function sortProductsBaseFirst<T extends { name: string; kind?: ProductKind | null }>(products: T[]): T[] {
  return [...products].sort((a, b) => {
    const aBase = a.kind === 'base' ? 0 : 1;
    const bBase = b.kind === 'base' ? 0 : 1;
    if (aBase !== bBase) return aBase - bBase;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}

export type PathListRow = { pathId: string; name: string; isDefault: boolean; journeyCount: number };
export type PathJourneyRow = { journeyId: string; order: number; name: string | null; levelName?: string | null };

/** The default learning path from `learningPaths.list` (null when none is marked default). */
export function pickDefaultPath<T extends { isDefault: boolean }>(paths: T[] | null | undefined): T | null {
  return paths?.find((p) => p.isDefault) ?? null;
}

/** "Iniciante → Básico 1 → Básico 2" in path order; unnamed journeys show the fallback. */
export function formatPathJourneys(journeys: PathJourneyRow[] | null | undefined, unnamed: string): string {
  return [...(journeys ?? [])]
    .sort((a, b) => a.order - b.order)
    .map((j) => (j.name && j.name.trim() ? j.name : unnamed))
    .join(' → ');
}
